import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges, Injector, OnDestroy } from '@angular/core';
import { MatDialogConfig } from '@angular/material/dialog';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { CommentsBoxEditComponent } from './comments-box-edit/comments-box-edit.component';
import { PlaceholdersService } from 'src/app/services/placeholders/placeholders.service';
import { FloatLabelType } from '@angular/material/form-field';
import { FormControl } from '@angular/forms';
import { ClBaseComponent } from '../checklist-components/cl-base/cl-base.component';
import { ImageUploadService } from 'src/app/services/file/image-upload.service';
import { Router } from '@angular/router';
import linkifyHtml from 'linkify-html';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Link } from 'src/app/components/catalogs/navigation-links/link/link';



@Component({
  selector: 'comments-box',
  templateUrl: './comments-box.component.html',
  styleUrls: ['./comments-box.component.scss']
})
export class CommentsBoxComponent extends ClBaseComponent implements OnChanges, OnDestroy {

  @Input() minHeight?: string;
  @Input() maxHeight?: string;
  @Input() hasError!: boolean;
  @Input() control?: FormControl;
  @Input() noSubscript?: boolean;
  @Input() placeholder?: string;
  @Input() cssClass?: string;
  @Input() noBorder?: boolean = false;
  @Input() hint?: SafeHtml | string | null;
  @Input() mentions?: any[];
  @Input() strikethroughMentionIds: number[] = [];
  @Input() hideShielding?: boolean;

  @Output() textChanged = new EventEmitter<any>();
  @Output() mentionsChanged = new EventEmitter<any>();
  @Output() cancelled = new EventEmitter();

  public loading!: boolean;
  textValueSanitized!: SafeHtml | null;
  selectedMentions: number[] = [];

  // tooltipText: string = "";
  // tooltipVisible: boolean = false;
  // tooltipElement?: HTMLElement;

  constructor(
    protected override injector: Injector,
    private sanitizer: DomSanitizer,
    private placeholdersService: PlaceholdersService,
    private service: ImageUploadService,
    private router: Router
  ) {
    super(injector);
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (this.textValue?.includes('data:image')) {
      const originalText = this.textValue;
      const text = await this.uploadImages(originalText);
      // Skip if the bound text changed during the upload (e.g. a different form was loaded) so stale text is not written into it.
      if (this.textValue === originalText && text != this.textValue) {
        this.textChanged.emit(text);
        this.textValue = text;
      }
    }
    this.setValue();
  }

  override ngOnDestroy() {
    this.closeMentionMenu();
    // this.hideTooltip();
  }

  setValue() {
    this.textValueSanitized = this.textValue ? this.transform(this.decorateStrikethroughMentions(this.textValue)) : null;
    // if (this.textValueSanitized)
    if (this.control) {
      if (this.disabled)
        this.control.disable();
      else {
        this.control.enable();
      }
      if (this.hasError) {
        this.control.setErrors({ required: true });
      };
    }
  }

  editText(e?: any, parent?: any) {
    e?.stopPropagation();
    const target = e?.target as HTMLElement;

    // Check if clicked on a mention
    const mentionElement = target?.closest('.mention') as HTMLElement;
    if (mentionElement) {
      let title = mentionElement.getAttribute('data-title') || '';
      const linksAttr = mentionElement.getAttribute('data-windchill-links');
      let windchillLinks: Link[] = [];

      if (linksAttr) {
        try {
          windchillLinks = JSON.parse(linksAttr);
        } catch {
          windchillLinks = [];
        }
      }

      // Fallback: look up from mentions array for legacy mentions missing attributes
      if (!title || windchillLinks.length === 0) {
        const id = Number(mentionElement.getAttribute('data-id'));
        const mention = this.mentions?.find(m => m.id === id);
        if (mention) {
          if (!title && mention.title) {
            title = mention.title;
            mentionElement.setAttribute('data-title', title);
          }
          if (windchillLinks.length === 0 && mention.windchillLinks?.length) {
            windchillLinks = mention.windchillLinks;
            mentionElement.setAttribute('data-windchill-links', JSON.stringify(windchillLinks));
          }
        }
      }

      // If we have anything to show, open the menu and stop here
      if (title || windchillLinks.length > 0) {
        e?.preventDefault();
        this.showMentionMenu(mentionElement, title, windchillLinks);
        return;
      }
    }

    const url = target?.getAttribute('href')!;
    if (target && target.tagName === 'A') {
      e?.preventDefault();
      // Check if the URL includes '/redirect/'
      if (url?.includes('/redirect/')) {
        const newUrl = url.replace(/.*?(\/redirect\/.*)/, '$1');
        // Ensure newUrl is formatted correctly
        const redirectRoute = newUrl.startsWith('/') ? newUrl : `/${newUrl}`;

        // Navigate to the new URL (this should trigger the guard)
        this.router.navigate([redirectRoute]);
      } else if (url.startsWith('http') || url.startsWith('www')) {
        // External URL: Use window.location.href
        const externalUrl = url.startsWith('www') ? `https://${url}` : url;
        window.open(externalUrl, '_blank');
      } else {
        // Internal route: Use Angular Router
        this.router.navigate([url]);
      }
    }
    else {
      const box = parent?.getBoundingClientRect();
      if (!this.disabled && !this.loading) {
        const dialogConfig = new MatDialogConfig();
        if (box) {
          const height = box.height + 52;
          let top = (((box.top ?? 0) - 38) > 0 ? ((box.top ?? 0) - 38) : 0);
          if (top + height > window.innerHeight) {
            top = window.innerHeight - height < 120 ? 120 : window.innerHeight - height;
          }
          dialogConfig.width = (box.width + 1) + 'px';
          dialogConfig.height = this.minHeight ? this.minHeight : height + 'px';
          dialogConfig.position = { left: (box.left ?? 0) + 'px', top: top + 'px' };
        }
        dialogConfig.autoFocus = false;
        dialogConfig.disableClose = true;
        dialogConfig.maxWidth = '100%';
        dialogConfig.maxHeight = (window.innerHeight * .9 - 105) + 'px';
        dialogConfig.minHeight = this.minHeight ? this.minHeight : '0';
        dialogConfig.data = {
          textValue: this.textValue,
          formType: this.formType,
          serialNo: this.serialNo,
          title: this.title,
          minHeight: this.minHeight,
          maxHeight: (window.innerHeight * .9 - 120) + 'px',
          mentions: this.mentions,
          selectedMentions: this.selectedMentions,
          strikethroughMentionIds: this.strikethroughMentionIds,
        };
        const dialogRef = this.dialog.open(CommentsBoxEditComponent, dialogConfig);
        dialogRef.afterClosed().subscribe(async (data) => {
          if (data) {
            this.loading = true;
            this.textValue = data.textValue;
            this.selectedMentions = data.selectedMentions;
            console.log('selectedMentions:', this.selectedMentions);

            this.emit(this.textValue, data.selectedMentions);
            this.hasError = this.textValue ? false : true;
          } else if (!this.textValue) {
            this.hasError = true;
          }
        });
      }
    }
  }

  private mentionMenuElement?: HTMLElement;

  showMentionMenu(anchorElement: HTMLElement, keText: string, links: Link[]) {
    this.closeMentionMenu();

    const rect = anchorElement.getBoundingClientRect();

    const escHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        this.closeMentionMenu();
        document.removeEventListener('keydown', escHandler);
      }
    };
    document.addEventListener('keydown', escHandler);

    this.mentionMenuElement = document.createElement('div');
    this.mentionMenuElement.classList.add('mention-info-menu');

    Object.assign(this.mentionMenuElement.style, {
      position: 'absolute',
      top: `${rect.bottom + window.scrollY + 4}px`,
      left: `${rect.left + window.scrollX}px`,
      backgroundColor: 'white',
      border: '1px solid #ddd',
      borderRadius: '4px',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
      zIndex: '10000',
      minWidth: '240px',
      maxWidth: '420px',
      padding: '4px 0',
      fontSize: '13px',
    });

    // KE Text section
    if (keText) {
      const keHeader = document.createElement('div');
      keHeader.textContent = 'KE Text';
      Object.assign(keHeader.style, {
        padding: '6px 12px 2px',
        fontWeight: 'bold',
        color: '#555',
        fontSize: '11px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
      });
      this.mentionMenuElement.appendChild(keHeader);

      const keBody = document.createElement('div');
      keBody.textContent = keText;
      Object.assign(keBody.style, {
        padding: '2px 12px 8px',
        color: '#333',
        whiteSpace: 'normal',
        lineHeight: '1.4',
      });
      this.mentionMenuElement.appendChild(keBody);
    }

    // Divider if both sections exist
    if (keText && links.length > 0) {
      const divider = document.createElement('div');
      Object.assign(divider.style, {
        borderTop: '1px solid #eee',
        margin: '2px 0',
      });
      this.mentionMenuElement.appendChild(divider);
    }

    // Windchill Links section
    if (links.length > 0) {
      const linksHeader = document.createElement('div');
      linksHeader.textContent = 'Windchill Links';
      Object.assign(linksHeader.style, {
        padding: '6px 12px 2px',
        fontWeight: 'bold',
        color: '#555',
        fontSize: '11px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
      });
      this.mentionMenuElement.appendChild(linksHeader);

      links.forEach(link => {
        const url = link.hyperlink || link.webAddress || '';
        const address = link.webAddress || '';
        const label = link.linkType?.name || '';

        const item = document.createElement('div');
        item.textContent = url;
        Object.assign(item.style, {
          padding: '6px 12px',
          cursor: url ? 'pointer' : 'default',
          color: url ? '#285E87' : '#999',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        });
        item.title = label;

        if (url) {
          item.addEventListener('mouseenter', () => {
            item.style.backgroundColor = '#f0f4f8';
          });
          item.addEventListener('mouseleave', () => {
            item.style.backgroundColor = 'transparent';
          });
          item.addEventListener('click', (e) => {
            e.stopPropagation();
            this.openLink(address);
            this.closeMentionMenu();
          });
        }

        this.mentionMenuElement!.appendChild(item);
      });
    }

    document.body.appendChild(this.mentionMenuElement);

    const menuRect = this.mentionMenuElement.getBoundingClientRect();
    if (menuRect.right > window.innerWidth - 10) {
      this.mentionMenuElement.style.left = `${window.innerWidth - menuRect.width - 10}px`;
    }
    if (menuRect.bottom > window.innerHeight - 10) {
      this.mentionMenuElement.style.top = `${rect.top + window.scrollY - menuRect.height - 4}px`;
    }

    setTimeout(() => {
      document.addEventListener('click', this.closeMentionMenuHandler, { once: true });
    });
  }

  private closeMentionMenuHandler = (e: MouseEvent) => {
    if (this.mentionMenuElement && !this.mentionMenuElement.contains(e.target as Node)) {
      this.closeMentionMenu();
    }
  };

  closeMentionMenu() {
    if (this.mentionMenuElement) {
      this.mentionMenuElement.remove();
      this.mentionMenuElement = undefined;
    }
    document.removeEventListener('click', this.closeMentionMenuHandler);
  }

  openLink(url: string) {
    if (url.includes('/redirect/')) {
      const newUrl = url.replace(/.*?(\/redirect\/.*)/, '$1');
      const redirectRoute = newUrl.startsWith('/') ? newUrl : `/${newUrl}`;
      this.router.navigate([redirectRoute]);
    } else if (url.startsWith('http') || url.startsWith('www')) {
      const externalUrl = url.startsWith('www') ? `https://${url}` : url;
      window.open(externalUrl, '_blank');
    } else {
      this.router.navigate([url]);
    }
  }

  emit(textValue: any, selectedMentions: any) {
    this.textChanged.emit(textValue);
    this.mentionsChanged.emit(selectedMentions);
    this.loading = false;
  }

  getFloatLabel(): FloatLabelType {
    return this.textValue ? 'always' : 'auto';
  }

  transform(value: string): SafeHtml | null {
    if (value) {
      const res = value?.replace(new RegExp('\{([a-z,A-Z,0-9]+)\}', 'ig'), match => {
        value = this.placeholdersService.getValue(match);
        if (value !== undefined && !this.editor) {
          return '<b style="border-bottom: 1px dotted #ddd; color: #285E87">' + value + '</b>';
        }
        else {
          return '<b style="color: #E04E39">' + match + '</b>';
        }
      });
      return this.sanitizer.bypassSecurityTrustHtml(linkifyHtml(res));
    }
    else {
      return null;
    }
  }

  /**
   * Applies a temporary strikeout to saved mentions that are no longer approved.
   * @param value - Saved work-description HTML.
   * @returns HTML decorated for display or editing without changing the persisted value.
   */
  private decorateStrikethroughMentions(value: string): string {
    if (!value) {
      return value;
    }

    const documentFragment = new DOMParser().parseFromString(value, 'text/html');
    documentFragment.querySelectorAll<HTMLElement>('span[data-id]').forEach(mention => {
      // Global classes override nested legacy Quill styles without rewriting saved formatting.
      mention.classList.add('mention-chip-medium');
      const mentionId = Number(mention.getAttribute('data-id'));
      if (this.strikethroughMentionIds.includes(mentionId)) {
        mention.classList.add('unapproved-toca-mention');
        mention.dataset['unapprovedTooltip'] = 'This TOCA is unapproved.';
      }
    });

    return documentFragment.body.innerHTML;
  }


  showTooltip(event: MouseEvent) {
    // const target = event.target as HTMLElement;
    // const mentionElement = target.closest('.mention') as HTMLElement;

    // if (mentionElement) {
    //   const title = mentionElement.getAttribute('data-title');
    //   if (title && this.tooltipText !== title) {
    //     this.hideTooltip();
    //   }
    //   if (title && !this.tooltipVisible) {
    //     this.tooltipText = title;
    //     this.tooltipVisible = true;
    //     this.showTooltipElement(event.clientX, event.clientY);
    //   }
    // }
    // else this.hideTooltip();
  }

  hideTooltip() {
    // this.tooltipVisible = false;
    // if (this.tooltipElement) {
    //   this.tooltipElement.remove();
    //   this.tooltipElement = undefined;
    // }
  }

  showTooltipElement(x: number, y: number) {
    // if (this.tooltipElement) {
    //   this.tooltipElement.remove();
    // }
    // this.tooltipElement = document.createElement('div');
    // this.tooltipElement.textContent = this.tooltipText;

    // // Check if the currently-hovered mention has links
    // const hovered = document.querySelector('.mention:hover') as HTMLElement;
    // if (hovered?.getAttribute('data-windchill-links')) {
    //   const hint = document.createElement('div');
    //   hint.textContent = 'Click to view Windchill links';
    //   hint.style.marginTop = '4px';
    //   hint.style.fontSize = '10px';
    //   hint.style.color = '#888';
    //   hint.style.fontStyle = 'italic';
    //   this.tooltipElement.appendChild(hint);
    // }

    // this.tooltipElement.style.position = 'absolute';
    // this.tooltipElement.style.top = `${y}px`;
    // this.tooltipElement.style.left = `${x}px`;
    // this.tooltipElement.style.backgroundColor = 'white';
    // this.tooltipElement.style.color = 'black';
    // this.tooltipElement.style.padding = '5px';
    // this.tooltipElement.style.borderRadius = '5px';
    // this.tooltipElement.style.zIndex = '1000';
    // this.tooltipElement.style.boxShadow = '0 2px 10px rgba(0, 0, 0, 0.2)';
    // this.tooltipElement.style.fontSize = '12px';
    // document.body.appendChild(this.tooltipElement);

    // this.tooltipElement.addEventListener('mouseout', () => {
    //   this.hideTooltip();
    // });
  }


  async uploadImages(textValue: string) {
    this.loading = true;
    const text = await this.service.uploadImagesToServer(textValue, '/' + this.formType?.toString()?.trim() + '/' + this.serialNo + '/Comments/');
    this.loading = false;
    return text;
  }

  onLinkClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (target.tagName === 'A') {
      event.preventDefault(); // Prevent default link behavior
      const url = target.getAttribute('href');
      // Implement your redirection logic here, for example:
      console.log(`Link clicked: ${url}`);
      // Optionally, you can use a service to handle routing or other logic
    }
  }
}
